import React, { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipProps } from "recharts";
import type { EngagementAnalyticsRow } from "../../../utils/engagementChart";

const TOOLTIP_SERIES = [
  { key: "connectionsSent" as const, label: "Connection requests sent", color: "#B4EA6B" },
  { key: "connectionsAccepted" as const, label: "Connections accepted", color: "var(--color-brand-blue)" },
  // { key: "messageReplies" as const, label: "Replies received", color: "var(--color-success)" },
  { key: "messagesSent" as const, label: "Messages sent", color: "#ec4899" },
  { key: "profileVisits" as const, label: "Profile visits", color: "var(--color-dark-blue)" },
];

function EngagementTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as EngagementAnalyticsRow & { totalEngagement: number };
  return (
    <div className="overflow-hidden rounded-lg border border-light-grey bg-white shadow-md">
      <div className="bg-primary px-3 py-1.5 text-xs font-medium text-dark-black">{row.tooltipDate}</div>
      <div className="space-y-1.5 px-3 py-2">
        {TOOLTIP_SERIES.map((series) => (
          <div key={series.key} className="flex items-center justify-between gap-6 text-xs">
            <span className="flex items-center gap-2 text-dark-grey">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: series.color }}
              />
              {series.label}
            </span>
            <span className="tabular-nums font-medium text-dark-black">{row[series.key]}</span>
          </div>
        ))}
        <div className="mt-1 border-t border-light-grey pt-1.5 text-xs">
          <div className="flex items-center justify-between gap-6">
            <span className="font-medium text-dark-grey">Total engagement</span>
            <span className="tabular-nums font-semibold text-dark-black">{row.totalEngagement}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

type EngagementAnalyticsGraphProps = {
  chartData: EngagementAnalyticsRow[];
  containerRef: React.RefObject<HTMLDivElement>;
};

export default function EngagementAnalyticsGraph({ chartData, containerRef }: EngagementAnalyticsGraphProps) {
  const graphData = useMemo(
    () =>
      chartData.map((row) => ({
        ...row,
        totalEngagement:
          row.connectionsSent +
          row.connectionsAccepted +
          row.messagesSent +
          // row.messageReplies +
          row.profileVisits,
      })),
    [chartData]
  );

  const yMax = useMemo(() => {
    let m = 0;
    for (const row of graphData) {
      m = Math.max(m, row.totalEngagement);
    }
    return Math.max(3, Math.ceil(m * 1.1) || 3);
  }, [graphData]);

  const xAxisInterval = useMemo(() => {
    const points = graphData.length;
    if (points <= 10) return 0;
    if (points <= 20) return 1;
    return Math.max(1, Math.floor(points / 8));
  }, [graphData.length]);

  return (
    <div ref={containerRef} className="h-[360px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={graphData} margin={{ top: 10, right: 20, left: 4, bottom: 4 }}>
          <defs>
            <linearGradient id="engagementAreaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#B4EA6B" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#B4EA6B" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="var(--color-light-grey)" vertical={false} strokeOpacity={0.35} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "var(--color-dark-grey)" }}
            interval={xAxisInterval}
            minTickGap={24}
            tickMargin={6}
            padding={{ left: 4, right: 14 }}
          />
          <YAxis
            domain={[0, yMax]}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "var(--color-dark-grey)" }}
            width={36}
          />
          <Tooltip
            content={<EngagementTooltip />}
            cursor={{ stroke: "var(--color-secondary)", strokeWidth: 1, strokeDasharray: "4 4" }}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="totalEngagement"
            name="Total engagement"
            stroke="#B4EA6B"
            strokeWidth={2.5}
            fill="url(#engagementAreaFill)"
            dot={false}
            activeDot={{ r: 4, stroke: "var(--color-white)", strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
